import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ActivityIndicator,
  Image,
  ScrollView
} from 'react-native';
import { Link, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { User, Mail, Lock, AlertCircle, ChevronLeft } from 'lucide-react-native';
import PasswordStrengthIndicator, { passwordCriteria } from '@/components/PasswordStrengthIndicator';

export default function RegisterScreen() {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { signUp, signInWithGoogle } = useAuth();
  const router = useRouter();
  const { colors } = useTheme();

  // Validações
  const isPasswordStrong = useMemo(() => {
    return passwordCriteria.every(criteria => criteria.test(senha));
  }, [senha]);

  const passwordsMatch = useMemo(() => {
    return senha === confirmarSenha && confirmarSenha.length > 0;
  }, [senha, confirmarSenha]);

  const showPasswordMismatch = useMemo(() => {
    return confirmarSenha.length > 0 && !passwordsMatch;
  }, [confirmarSenha, passwordsMatch]);

  const isFormValid = useMemo(() => {
    return nome.trim().length > 0 &&
      email.trim().length > 0 &&
      isPasswordStrong &&
      passwordsMatch;
  }, [nome, email, isPasswordStrong, passwordsMatch]);

  const handleSignUp = async () => {
    if (!isFormValid) {
      Alert.alert('Erro', 'Por favor, preencha todos os campos corretamente.');
      return;
    }

    setLoading(true);
    try {
      await signUp(nome, email, senha);
      if (Platform.OS === 'web') {
        router.replace({ pathname: '/(auth)/login', params: { signedUp: '1', email } } as any);
      } else {
        Alert.alert(
          'Sucesso!',
          'Sua conta foi criada. Um email de verificação foi enviado.',
          [{ text: 'OK', onPress: () => router.push('/(auth)/login') }]
        );
      }
    } catch (error: any) {
      Alert.alert('Erro no Cadastro', error.message || 'Não foi possível criar a conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Indisponível', 'Login com Google está disponível apenas na versão web por enquanto.');
      return;
    }

    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      if (error.message) {
        Alert.alert('Erro', error.message);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    backButton: {
      position: 'absolute',
      top: 24,
      left: 24,
      padding: 12,
      borderRadius: 24,
      zIndex: 10,
    },
    keyboardView: {
      flex: 1,
    },
    scrollView: {
      flex: 1,
    },
    content: {
      flex: 1,
      paddingHorizontal: 24,
      justifyContent: 'center',
      minHeight: '100%',
    },
    header: {
      alignItems: 'center',
      marginBottom: 48,
    },
    logo: {
      width: 80,
      height: 80,
      marginBottom: 16,
    },
    title: {
      fontSize: 28,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      marginBottom: 8,
    },
    subtitle: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
    },
    form: {
      gap: 16,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 56,
      borderWidth: 1,
      borderColor: colors.border,
    },
    inputIcon: {
      marginRight: 12,
    },
    input: {
      flex: 1,
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
    },
    registerButton: {
      backgroundColor: colors.primary,
      borderRadius: 12,
      height: 56,
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 8,
      flexDirection: 'row',
    },
    registerButtonDisabled: {
      opacity: 0.6,
    },
    registerButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
    termsContainer: {
      alignItems: 'center',
      marginTop: 16,
      paddingHorizontal: 8,
    },
    termsText: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 18,
    },
    termsLinks: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
    },
    termsLink: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
      textDecorationLine: 'underline',
    },
    loginContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 24,
    },
    loginText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    loginLink: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    errorContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginTop: 8,
    },
    errorText: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: '#EF4444',
    },
    button: {
      borderRadius: 12,
      height: 56,
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 8,
      flexDirection: 'row',
    },
    buttonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
    dividerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: 20,
    },
    dividerLine: {
      flex: 1,
      height: 1,
      backgroundColor: colors.border,
    },
    dividerText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginHorizontal: 16,
    },
    googleButton: {
      backgroundColor: '#FFFFFF',
      borderRadius: 12,
      height: 56,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
    },
    googleButtonDisabled: {
      opacity: 0.6,
    },
    googleIcon: {
      width: 20,
      height: 20,
      marginRight: 12,
    },
    googleButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#1F1F1F',
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <TouchableOpacity
        style={[styles.backButton, { backgroundColor: colors.surface }]}
        activeOpacity={0.7}
        onPress={() => router.back()}
      >
        <ChevronLeft size={20} color={colors.text} />
      </TouchableOpacity>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.content}>
            <View style={styles.header}>
              <Image
                source={require('@/public/icon-pages.png')}
                style={styles.logo}
                resizeMode="contain"
              />
              <Text style={styles.title}>Criar Conta</Text>
              <Text style={styles.subtitle}>Junte-se à comunidade do Põe no Racha</Text>
            </View>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <User size={20} color={colors.textSecondary} style={styles.inputIcon} />
                <TextInput
                  style={styles.input}
                  placeholder="Nome completo"
                  placeholderTextColor={colors.textSecondary}
                  value={nome}
                  onChangeText={setNome}
                  autoCapitalize="words"
                  editable={!loading}
                />
              </View>

              <View style={styles.inputContainer}>
                <Mail size={20} color={colors.textSecondary} style={styles.inputIcon} />
                <TextInput
                  style={styles.input}
                  placeholder="Email"
                  placeholderTextColor={colors.textSecondary}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  editable={!loading}
                />
              </View>

              <View style={styles.inputContainer}>
                <Lock size={20} color={colors.textSecondary} style={styles.inputIcon} />
                <TextInput
                  style={[styles.input, { color: colors.text, borderColor: colors.border }]}
                  placeholder="Senha"
                  placeholderTextColor={colors.textSecondary}
                  value={senha}
                  onChangeText={setSenha}
                  secureTextEntry
                  autoCapitalize="none"
                />
              </View>

              {senha.length > 0 && (
                <PasswordStrengthIndicator password={senha} />
              )}

              <View style={styles.inputContainer}>
                <Lock size={20} color={colors.textSecondary} style={styles.inputIcon} />
                <TextInput
                  style={[
                    styles.input,
                    {
                      color: colors.text,
                      borderColor: showPasswordMismatch ? '#EF4444' : colors.border
                    }
                  ]}
                  placeholder="Confirmar Senha"
                  placeholderTextColor={colors.textSecondary}
                  value={confirmarSenha}
                  onChangeText={setConfirmarSenha}
                  secureTextEntry
                  autoCapitalize="none"
                />
              </View>

              {showPasswordMismatch && (
                <View style={styles.errorContainer}>
                  <AlertCircle size={16} color="#EF4444" />
                  <Text style={styles.errorText}>As senhas não coincidem</Text>
                </View>
              )}

              <TouchableOpacity
                style={[
                  styles.button,
                  {
                    backgroundColor: isFormValid ? colors.primary : colors.textSecondary,
                    opacity: loading ? 0.7 : 1,
                  },
                ]}
                onPress={handleSignUp}
                disabled={loading || googleLoading || !isFormValid}
              >
                {loading ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.buttonText}>Cadastrar</Text>
                )}
              </TouchableOpacity>

              {/* Separador */}
              <View style={styles.dividerContainer}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>ou</Text>
                <View style={styles.dividerLine} />
              </View>

              {/* Botão Google */}
              <TouchableOpacity
                style={[styles.googleButton, googleLoading && styles.googleButtonDisabled]}
                onPress={handleGoogleSignIn}
                disabled={loading || googleLoading}
                activeOpacity={0.7}
              >
                {googleLoading ? (
                  <ActivityIndicator size="small" color="#1F1F1F" />
                ) : (
                  <>
                    <Image
                      source={{ uri: 'https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg' }}
                      style={styles.googleIcon}
                      resizeMode="contain"
                    />
                    <Text style={styles.googleButtonText}>Cadastrar com Google</Text>
                  </>
                )}
              </TouchableOpacity>

              <View style={styles.termsContainer}>
                <Text style={styles.termsText}>Ao criar uma conta, você concorda com nossos </Text>
                <View style={styles.termsLinks}>
                  <Link href="/termos-de-uso" asChild>
                    <TouchableOpacity disabled={loading}>
                      <Text style={styles.termsLink}>Termos de Uso</Text>
                    </TouchableOpacity>
                  </Link>
                  <Text style={styles.termsText}> e </Text>
                  <Link href="/politica-privacidade" asChild>
                    <TouchableOpacity disabled={loading}>
                      <Text style={styles.termsLink}>Política de Privacidade</Text>
                    </TouchableOpacity>
                  </Link>
                </View>
              </View>

              <View style={styles.loginContainer}>
                <Text style={styles.loginText}>Já tem uma conta? </Text>
                <Link href="/(auth)/login" asChild>
                  <TouchableOpacity disabled={loading}>
                    <Text style={styles.loginLink}>Entrar</Text>
                  </TouchableOpacity>
                </Link>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
